import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Upload, Button, FormInstance, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import axiosRequest from '@/api';
import { ResData } from '@/@types';
import { BizUpload } from '@/@types/BizUpload';
import * as S from './index.styled';
import { PartnerCompany } from '@/@types/PartnerCompany';

const { Option } = Select;

interface InformationInputNewCompanyProps {
  form: FormInstance;
  stepData: (data: Record<string, any>) => void;
  category: string;
  upperAgency: string | null;
  inputBizNo: { part1: string; part2: string; part3: string };
  setStepValid: (isValid: boolean) => void;
}

const InformationInputNewCompany: React.FC<InformationInputNewCompanyProps> = ({
  form,
  stepData,
  category,
  inputBizNo,
  setStepValid,
}) => {
  const [uploading, setUploading] = useState(false);
  const [upperAdvertisers, setUpperAdvertisers] = useState<PartnerCompany[]>([]);
  const [fileName, setFileName] = useState<string>('');

  useEffect(() => {
    const fetchUpperAdvertisers = async () => {
      try {
        const response = await axiosRequest.requestAxios<ResData<PartnerCompany[]>>(
          'get',
          `/partner_company/${category}`,
        );
        setUpperAdvertisers(response.data);
      } catch (error) {
        message.error('상위 대행사 정보를 가져오는 데 실패했습니다.');
      }
    };

    fetchUpperAdvertisers();
  }, [category]);

  const handleUpload = async (options: any) => {
    const { file, onSuccess, onError } = options;

    if (
      file.type !== 'application/pdf' &&
      file.type !== 'image/png' &&
      file.type !== 'image/jpeg' &&
      file.type !== 'image/bmp'
    ) {
      message.error('Only PDF, PNG, JPEG, BMP files are allowed.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await axiosRequest.requestAxios<ResData<BizUpload>>('post', '/upload_bizdoc', formData);

      form.setFieldsValue({ ad_biz_enroll_docs: response.data.doc_url });
      setFileName(file.name);
      message.success('파일 업로드가 성공하였습니다.');
      if (onSuccess) {
        onSuccess(response.data, new XMLHttpRequest());
      }
    } catch (error) {
      message.error('파일 업로드가 실패하였습니다.');
      if (onError) {
        onError(error);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSelectChange = (value: string) => {
    const selectedAdvertiser = upperAdvertisers.find(ad => ad.id.toString() === value);
    if (selectedAdvertiser) {
      form.setFieldsValue({ upperAgencyName: selectedAdvertiser.name });
      stepData({ ...form.getFieldsValue(), upperAgency: value, partner_id: selectedAdvertiser.id });
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      bizNo: `${inputBizNo.part1}-${inputBizNo.part2}-${inputBizNo.part3}`,
    });
  }, [form, inputBizNo]);

  const handleValuesChange = () => {
    const fields = form.getFieldsValue();
    const isValid =
      fields.ad_name &&
      fields.ad_ceo_name &&
      fields.ad_biz_type &&
      fields.ad_biz_item &&
      fields.ad_address &&
      fields.ad_biz_enroll_docs;
    setStepValid(isValid);
  };

  return (
    <S.Container>
      <S.FormTitle>회사 정보</S.FormTitle>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
      >
        <S.StyledTable>
          <tbody>
            {/* <tr>
              <td>상위 대행사</td>
              <td>
                <S.StyledFormItem
                  name="upperAgency"
                  rules={[{ required: true, message: '상위 대행사를 선택해주세요.' }]}
                >
                  <S.StyledSelect placeholder="선택해주세요" style={{ width: '100%' }} onChange={handleSelectChange}>
                    {upperAdvertisers.map(ad => (
                      <Option key={ad.id} value={ad.id.toString()}>
                        {ad.name}
                      </Option>
                    ))}
                  </S.StyledSelect>
                </S.StyledFormItem>
                <S.StyledFormItem name="upperAgencyName" style={{ display: 'none' }}>
                  <S.StyledInput readOnly />
                </S.StyledFormItem>
              </td>
            </tr> */}
            <tr>
              <td>사업자등록번호</td>
              <td>{`${inputBizNo.part1}-${inputBizNo.part2}-${inputBizNo.part3}`}</td>
            </tr>
            <tr>
              <td>기업명</td>
              <td>
                <S.StyledFormItem name="ad_name" rules={[{ required: true, message: '기업명을 입력해주세요.' }]}>
                  <S.StyledInput />
                </S.StyledFormItem>
              </td>
            </tr>
            <tr>
              <td>대표자명</td>
              <td>
                <S.StyledFormItem name="ad_ceo_name" rules={[{ required: true, message: '대표자명을 입력해주세요.' }]}>
                  <S.StyledInput />
                </S.StyledFormItem>
              </td>
            </tr>
            <tr>
              <td>업태</td>
              <td>
                <S.StyledFormItem name="ad_biz_type" rules={[{ required: true, message: '업태를 입력해주세요.' }]}>
                  <S.StyledInput />
                </S.StyledFormItem>
              </td>
            </tr>
            <tr>
              <td>업종</td>
              <td>
                <S.StyledFormItem name="ad_biz_item" rules={[{ required: true, message: '업종을 입력해주세요.' }]}>
                  <S.StyledInput />
                </S.StyledFormItem>
              </td>
            </tr>
            <tr>
              <td>주소</td>
              <td>
                <S.StyledFormItem name="ad_address" rules={[{ required: true, message: '주소를 입력해주세요.' }]}>
                  <S.StyledInput />
                </S.StyledFormItem>
              </td>
            </tr>
            <tr>
              <td>사업자등록증</td>
              <td>
                <S.StyledFormItem
                  name="ad_biz_enroll_docs"
                  rules={[{ required: true, message: '사업자등록증을 업로드해주세요.' }]}
                >
                  <S.FlexContainer>
                    <Upload customRequest={handleUpload} showUploadList={false} accept=".pdf,.png,.jpeg,.jpg,.bmp">
                      <Button icon={<UploadOutlined />} loading={uploading}>
                        파일 선택
                      </Button>
                    </Upload>
                    <S.FileName>{fileName || 'pdf, png, jpeg, jpg, bmp 업로드 가능'}</S.FileName>
                  </S.FlexContainer>
                </S.StyledFormItem>
              </td>
            </tr>
          </tbody>
        </S.StyledTable>
      </Form>
    </S.Container>
  );
};

export default InformationInputNewCompany;
